import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Bienvenue ${u.name}`);
      nav(u.is_merchant ? "/merchant" : "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Bon retour" subtitle="Connectez-vous pour gérer vos commerces et promotions.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label className="text-xs uppercase tracking-wider">Email</Label>
          <Input data-testid="login-email" type="email" required value={email} onChange={e=>setEmail(e.target.value)} className="mt-1 rounded-xl"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Mot de passe</Label>
          <Input data-testid="login-password" type="password" required value={password} onChange={e=>setPassword(e.target.value)} className="mt-1 rounded-xl"/>
        </div>
        <Button type="submit" data-testid="login-submit" disabled={loading}
          className="w-full rounded-full bg-[#E26D5C] hover:bg-[#D15B4A] text-white font-semibold py-6">
          {loading ? "Connexion…" : "Se connecter"}
        </Button>
        <p className="text-sm text-center text-[#5C6B55]">
          Pas encore de compte ? <Link to="/register" data-testid="to-register" className="text-[#E26D5C] font-semibold hover:underline">Inscrivez-vous</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", is_merchant: true });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Compte créé ! Bienvenue ${u.name}`);
      nav(u.is_merchant ? "/merchant" : "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Créer un compte" subtitle="Rejoignez la communauté Réunion Discover.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div>
          <Label className="text-xs uppercase tracking-wider">Nom</Label>
          <Input data-testid="register-name" required value={form.name} onChange={e=>setForm({...form, name: e.target.value})} className="mt-1 rounded-xl"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Email</Label>
          <Input data-testid="register-email" type="email" required value={form.email} onChange={e=>setForm({...form, email: e.target.value})} className="mt-1 rounded-xl"/>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Mot de passe</Label>
          <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={e=>setForm({...form, password: e.target.value})} className="mt-1 rounded-xl"/>
        </div>
        <div className="flex items-center justify-between rounded-xl bg-[#F3F0EA] px-4 py-3">
          <div>
            <div className="font-semibold text-sm">Je suis un commerçant</div>
            <div className="text-xs text-[#5C6B55]">Ajoutez et boostez vos établissements</div>
          </div>
          <Switch data-testid="register-merchant-toggle" checked={form.is_merchant} onCheckedChange={(v)=>setForm({...form, is_merchant: v})} />
        </div>
        <Button type="submit" data-testid="register-submit" disabled={loading}
          className="w-full rounded-full bg-[#E26D5C] hover:bg-[#D15B4A] text-white font-semibold py-6">
          {loading ? "Création…" : "Créer mon compte"}
        </Button>
        <p className="text-sm text-center text-[#5C6B55]">
          Déjà inscrit ? <Link to="/login" data-testid="to-login" className="text-[#E26D5C] font-semibold hover:underline">Connexion</Link>
        </p>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex" data-testid="auth-shell">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src="https://images.unsplash.com/photo-1683020369607-3ece7bf5f364?w=1400" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2C3627]/70 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="uppercase-label">Sors · Découvre · Kiffe</div>
          <div className="font-display font-black text-4xl tracking-tighter mt-1">
            Du Piton de la Fournaise<br/>aux plages turquoise.
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8 pt-28 lg:pt-8">
        <div className="w-full max-w-md">
          <div className="uppercase-label text-[#FF6B35]">RunSpot · 974</div>
          <h1 className="font-display font-black text-4xl tracking-tighter text-[#2C3627] mt-1">{title}</h1>
          <p className="text-[#5C6B55] mt-2">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
